import { Preset, PresetDefinition } from './Preset.interface';
import { OPTIONS_FOR_SECTOR, OPTIONS_FOR_SECTOR_SHORT } from './EconomicSector.data';
import { OPTIONS_FOR_LEGAL_STATUS, OPTIONS_FOR_LEGAL_STATUS_SHORT } from '../features/legal-status/LegalStatus.data';

export const DEFAULT_PRESET: Preset = 'ProEnt';

/**
 * Definition of the PRESETS available in the application.
 * We should never rely on the direct value in the features and other components.
 * For example something like if (preset === 'MicroEnt') is forbidden.
 * We should always use the 'abstract' capabilities that are defined below.
 */
export const PRESETS: Record<Preset, PresetDefinition> = {
    MicroEnt: {
        type: 'MicroEnt',
        captcha: {},
        pricing: {},
        screens: {
            hasPeculiarities: {},
            businessPremisesPresence: {},
            businessPremisesOwnership: {},
            businessPremisesSurface: {},
            cumulativeValueOfGoods: {},
            economicSector: {
                options: OPTIONS_FOR_SECTOR_SHORT,
            },
            legalStatus: {
                options: OPTIONS_FOR_LEGAL_STATUS_SHORT,
            },
            income: {
                display: 'manual',
            },
        },
        stepper: {
            data: {
                'Company': {
                    activeStep: 0,
                    stepProgress: 7,
                },
                'PreEconomicSector': {
                    activeStep: 0,
                    stepProgress: 14,
                },
                'EconomicSector': {
                    activeStep: 0,
                    stepProgress: 14,
                },
                'PreLegalStatus': {
                    activeStep: 0,
                    stepProgress: 29,
                },
                'LegalStatus': {
                    activeStep: 0,
                    stepProgress: 29,
                },
                'HasEmployees': {
                    activeStep: 0,
                    stepProgress: 43,
                },
                'Income': {
                    activeStep: 0,
                    stepProgress: 57,
                },
                'HasPeculiarities': {
                    activeStep: 0,
                    stepProgress: 71,
                },
                'CumulativeValueOfGoods': {
                    activeStep: 0,
                    stepProgress: 86,
                },
                'BusinessPremisesPresence': {
                    activeStep: 1,
                    stepProgress: 25,
                },
                'BusinessPremisesOwnership': {
                    activeStep: 1,
                    stepProgress: 50,
                },
                'BusinessPremisesSurface': {
                    activeStep: 1,
                    stepProgress: 75,
                },
                'Contact': {
                    activeStep: 2,
                    stepProgress: 50,
                },
                'Tarification': null,
                'PreConfirmation': null,
                'End/Confirmation': null,
                'End/AxaPartner': null,
                'End/Reject': null,
                'End/Unreliable-contact-information': null,
                'End/Apart-from-metropolis': null,
                'End/Rejet-sec': null,
                'End/Rejet-vtc': null,
                'End/Out-of-bounds': null,
                'End/Insurance': null,
                'End/Off-target-product': null,
                'Error': null,
                'Scenarios': null,
                'RecommandationHome': null,
                'Catalog': null,
                'CatalogRecommandation': null,
                'ChooseAdvisor': null,
                'PreCatalogRecommandation': null,
                'PreChooseAdvisor': null,
                'PreTarification': null,
                'YearsOfExperience': null,
                'NumberOfEmployees': null,
            },
            steps: [
                'Votre entreprise',
                'Votre local',
                'Vos coordonnées',
                'Votre tarification',
            ],
        },
    },
    ProEnt: {
        type: 'ProEnt',
        screens: {
            catalog: {},
            economicSector: {
                options: OPTIONS_FOR_SECTOR,
            },
            legalStatus: {
                options: OPTIONS_FOR_LEGAL_STATUS,
            },
            income: {
                display: 'dropdown',
            },
        },
        stepper: {
            data: {
                'Catalog': {
                    activeStep: 0,
                    stepProgress: 20,
                },
                'Company': {
                    activeStep: 1,
                    stepProgress: 10,
                },
                'PreEconomicSector': {
                    activeStep: 1,
                    stepProgress: 20,
                },
                'EconomicSector': {
                    activeStep: 1,
                    stepProgress: 20,
                },
                'PreLegalStatus': {
                    activeStep: 1,
                    stepProgress: 25,
                },
                'LegalStatus': {
                    activeStep: 1,
                    stepProgress: 30,
                },
                'NumberOfEmployees': {
                    activeStep: 1,
                    stepProgress: 50,
                },
                'Income': {
                    activeStep: 1,
                    stepProgress: 70,
                },
                'YearsOfExperience': {
                    activeStep: 1,
                    stepProgress: 90,
                },
                'PreCatalogRecommandation': {
                    activeStep: 1,
                    stepProgress: 95,
                },
                'CatalogRecommandation': {
                    activeStep: 1,
                    stepProgress: 95,
                },
                'Contact': {
                    activeStep: 2,
                    stepProgress: 20,
                },
                'PreChooseAdvisor': {
                    activeStep: 2,
                    stepProgress: 100,
                },
                'ChooseAdvisor': {
                    activeStep: 2,
                    stepProgress: 100,
                },
                'PreConfirmation': null,
                'End/Confirmation': null,
                'End/AxaPartner': null,
                'End/Reject': null,
                'End/Unreliable-contact-information': null,
                'End/Apart-from-metropolis': null,
                'End/Rejet-sec': null,
                'End/Rejet-vtc': null,
                'End/Out-of-bounds': null,
                'End/Insurance': null,
                'End/Off-target-product': null,
                'Error': null,
                'Scenarios': null,
                'RecommandationHome': null,
                'HasEmployees': null,
                'HasPeculiarities': null,
                'CumulativeValueOfGoods': null,
                'BusinessPremisesPresence': null,
                'BusinessPremisesOwnership': null,
                'BusinessPremisesSurface': null,
                'PreTarification': null,
                'Tarification': null,
            },
            steps: [
                'Votre besoin',
                'Votre entreprise',
                'Vos coordonnées',
            ],
        },
    },
};

/**
 * Select the preset or fallbacks on the default value (@see DEFAULT_PRESET)
 * @param value
 * @returns
 */
export function selectPreset(value: Preset | string | undefined): PresetDefinition {
    if (!value) {
        return PRESETS[ DEFAULT_PRESET ];
    }

    return PRESETS[ value as Preset ] || PRESETS[ DEFAULT_PRESET ];
}
